import type { ListViewPath } from "_shared/_views/list-pages/current-table-view";
import {
  readListChips,
  writeListChips,
  type ListChipGroup,
  type ListChipSelection,
} from "_shared/_views/list-pages/list-chip-selection";
import { retiredListPath, type RetiredListSegment } from "_shared/_views/list-pages/retired-list-redirect";
import type { CurrentTableViewOptions } from "_shared/_views/list-pages/use-current-table-view";
import { BoxType } from "notifications/notification-box-type";

/**
 * The tabs and chips of the Communications page, and how its URL spells them (#6719).
 *
 * The page had four tabs, Inbox, Sent, Conversations and Archived, for two kinds of thing. A tab is a
 * different kind of thing, so the tabs are Notifications and Conversations, and the notification boxes
 * became chips. With no chip the Notifications tab is the reader's inbox. Sent and Archived each
 * replace the inbox, and list both together. Unread and Action required narrow the inbox, and each
 * other; Sent and Archived hide them.
 */

/** The page's path, which is its Notifications tab. */
export const COMMUNICATIONS_PATH = "/communications";

/** The Conversations tab's path segment. */
export const CONVERSATIONS_TAB = "conversations";

/** The Conversations tab's path. */
export const CONVERSATIONS_PATH = `${COMMUNICATIONS_PATH}/${CONVERSATIONS_TAB}`;

/** The page's two tabs. */
export enum CommunicationsTab {
  Notifications = "notifications",
  Conversations = "conversations",
}

/** The query parameter of the box chips. */
export const BOX_PARAMETER = "box";

/** The Sent chip's value: what the reader sent. */
export const SENT_BOX = "sent";

/** The Archived chip's value: what the reader archived, received and sent alike. */
export const ARCHIVED_BOX = "archived";

/**
 * The box a link writes for the inbox: no chip, said outright, so the reader's remembered view does
 * not replace it (#6714). No chip has it, so the page reads it as no chip, as the work agreement list
 * reads `stage=open` (#6716).
 */
export const INBOX_BOX = "inbox";

/** The query parameter of the Unread chip, which the inbox's links have always written. */
export const UNREAD_PARAMETER = "unread";

/** The query parameter of the Action required chip, which the inbox's links have always written. */
export const ACTION_REQUIRED_PARAMETER = "actionRequired";

/** The value an on Unread or Action required chip is spelled with. */
const ON = "true";

/** The inbox's own chips are offered only in the inbox: a sent notification is read and asks nothing. */
function inboxOnly(selection: ListChipSelection): readonly string[] {
  return listsInbox(selection) ? [ON] : [];
}

export const communicationChipGroups: readonly ListChipGroup[] = [
  {
    parameter: BOX_PARAMETER,
    label: "Box",
    // Nothing chosen is the inbox, which the Box menu names (#6720).
    allLabel: "Inbox",
    chips: [
      { value: SENT_BOX, label: "Sent" },
      { value: ARCHIVED_BOX, label: "Archived" },
    ],
  },
  { parameter: UNREAD_PARAMETER, label: "Read", chips: [{ value: ON, label: "Unread" }], chipsGiven: inboxOnly },
  {
    parameter: ACTION_REQUIRED_PARAMETER,
    label: "Action",
    chips: [{ value: ON, label: "Action required" }],
    chipsGiven: inboxOnly,
  },
];

/** The Conversations tab's one chip. Its parameter is the one the forum list has always read (#6583). */
export const conversationChipGroups: readonly ListChipGroup[] = [
  { parameter: UNREAD_PARAMETER, label: "Read", chips: [{ value: ON, label: "Unread" }] },
];

/** The chips `searchParams` names. */
export function readCommunicationChips(searchParams: URLSearchParams): ListChipSelection {
  return readListChips(communicationChipGroups, searchParams);
}

/** The box chips that are on. A selection that names no box group, as Clear's does, has none. */
export function chosenBoxes(selection: ListChipSelection): readonly string[] {
  return selection[BOX_PARAMETER] ?? [];
}

/** Whether `selection` lists the inbox: no box chip is on. */
export function listsInbox(selection: ListChipSelection): boolean {
  return chosenBoxes(selection).length === 0;
}

/** Whether a group whose single chip is spelled `true` is on. */
export function isChipOn(selection: ListChipSelection, parameter: string): boolean {
  return selection[parameter]?.includes(ON) === true;
}

/** The boxes the API is asked for: the inbox with no box chip, else each chip's box. */
export function boxTypesOf(selection: ListChipSelection): BoxType[] {
  const boxes = chosenBoxes(selection);
  if (boxes.length === 0) return [BoxType.Inbox];
  return [
    ...(boxes.includes(SENT_BOX) ? [BoxType.Outbox] : []),
    ...(boxes.includes(ARCHIVED_BOX) ? [BoxType.Archive] : []),
  ];
}

/**
 * The people columns the boxes show. The inbox shows who it is from, since it is to the reader, and
 * Sent who it went to, since it is from the reader. The archive holds both, so it shows both.
 */
export function communicationPeopleColumns(selection: ListChipSelection): Readonly<{ from: boolean; to: boolean }> {
  const boxes = chosenBoxes(selection);
  return {
    from: boxes.length === 0 || boxes.includes(ARCHIVED_BOX),
    to: boxes.length > 0,
  };
}

/** What the search box searches, in the reader's words. */
export function communicationsSearchPlaceholder(selection: ListChipSelection): string {
  const boxes = chosenBoxes(selection);
  if (boxes.includes(SENT_BOX) && boxes.includes(ARCHIVED_BOX)) return "Search sent and archived";
  if (boxes.includes(SENT_BOX)) return "Search what you sent";
  if (boxes.includes(ARCHIVED_BOX)) return "Search archived notifications";
  return "Search your inbox";
}

/** What the list holds, for its empty state: "No matching {subject} found". */
export function communicationsSubject(selection: ListChipSelection): string {
  const boxes = chosenBoxes(selection);
  if (boxes.includes(SENT_BOX) && boxes.includes(ARCHIVED_BOX)) return "sent or archived notifications";
  if (boxes.includes(SENT_BOX)) return "sent notifications";
  if (boxes.includes(ARCHIVED_BOX)) return "archived notifications";
  return "notifications in your inbox";
}

/**
 * `searchParams` with `selection` written over the chips. The list starts again at its first page, and
 * a From or To filter the new boxes have no column for is dropped, since nothing would show it.
 */
export function writeCommunicationChips(selection: ListChipSelection, searchParams: URLSearchParams): URLSearchParams {
  const next = writeListChips(communicationChipGroups, selection, searchParams);
  const columns = communicationPeopleColumns(selection);
  if (!columns.from) next.delete("from");
  if (!columns.to) next.delete("to");
  if (next.has("pageIndex")) next.set("pageIndex", "0");
  return next;
}

/**
 * The tabs the page had before #6719. Bookmarks, notification emails and the assistant's answers name
 * them, so each still opens the rows it listed, whatever view the reader kept. The compose and
 * conversation routes under them, such as `/communications/forums/view/:id`, are other routes and keep
 * resolving.
 */
export const RETIRED_COMMUNICATIONS_TABS: readonly RetiredListSegment[] = [
  // The inbox said outright, as `INBOX_BOX` is: a bare `/communications` opens the reader's kept view,
  // which can be their sent notifications. The link's Unread and Action required are kept.
  { segment: "inbox", chips: { [BOX_PARAMETER]: [INBOX_BOX] } },
  { segment: BoxType.Outbox, chips: { [BOX_PARAMETER]: [SENT_BOX] } },
  { segment: BoxType.Archive, chips: { [BOX_PARAMETER]: [ARCHIVED_BOX] } },
  { segment: BoxType.Forums, tab: CONVERSATIONS_TAB },
];

/**
 * Where a link to `/communications/{segment}` opens now: a retired tab's new place, the page itself
 * for a segment it never had, or undefined for the Conversations tab and the compose dialog, which the
 * page shows.
 */
export function communicationsListPath(segment: string | undefined, searchParams: URLSearchParams): string | undefined {
  if (segment === undefined) return undefined;
  const named = segment.toLowerCase();
  if (named === CONVERSATIONS_TAB || named === COMPOSE_SEGMENT) return undefined;
  const retired = retiredListPath(COMMUNICATIONS_PATH, segment, searchParams, RETIRED_COMMUNICATIONS_TABS);
  if (retired) return retired;
  const query = searchParams.toString();
  return query ? `${COMMUNICATIONS_PATH}?${query}` : COMMUNICATIONS_PATH;
}

/** The Notifications tab's compose segment: `/communications/compose`. */
export const COMPOSE_SEGMENT = "compose";

/**
 * The tab a route's first segment is on. The retired tabs' compose and conversation routes keep
 * resolving: under `inbox`, `outbox` and `archive` they are on Notifications, under `forums` on
 * Conversations.
 */
export function communicationsTabOfSegment(segment: string | undefined): CommunicationsTab {
  const named = segment?.toLowerCase();
  return named === CONVERSATIONS_TAB || named === BoxType.Forums
    ? CommunicationsTab.Conversations
    : CommunicationsTab.Notifications;
}

/** The path of a tab, which is also the path its compose dialog is opened under. */
export function communicationsTabPath(tab: CommunicationsTab): string {
  return tab === CommunicationsTab.Conversations ? CONVERSATIONS_PATH : COMMUNICATIONS_PATH;
}

/** Where New Message opens the compose dialog: over the tab, and over the list as the reader has it. */
export function communicationsComposePath(tab: CommunicationsTab, search: string): string {
  const path =
    tab === CommunicationsTab.Conversations ? `${CONVERSATIONS_PATH}/${COMPOSE_SEGMENT}` : `${COMMUNICATIONS_PATH}/${COMPOSE_SEGMENT}`;
  return `${path}${search}`;
}

/**
 * The table's parameters a stored view of the Notifications tab keeps: its column filters, its sort
 * and its page size. The chips are kept too, below. The search text and the page number are never
 * kept (#6714).
 */
const KEPT_PARAMETERS: readonly string[] = ["type", "from", "to", "description", "orderBy", "orderDir", "pageSize"];

/**
 * A stored view as this page still reads it: on one of its two tabs, with a chip this page no longer
 * has dropped. A view of a tab the page no longer has, kept before #6719, is shown as the chips that
 * tab became.
 */
export function keepCommunicationsView(view: ListViewPath): ListViewPath | undefined {
  if (view.tabPath === CONVERSATIONS_PATH) return view;
  const segment = view.tabPath.startsWith(`${COMMUNICATIONS_PATH}/`)
    ? view.tabPath.slice(COMMUNICATIONS_PATH.length + 1)
    : undefined;
  if (view.tabPath !== COMMUNICATIONS_PATH && segment === undefined) return undefined;

  const stored = new URLSearchParams(view.search);
  const retired = segment === undefined ? undefined : RETIRED_COMMUNICATIONS_TABS.find((tab) => tab.segment === segment);
  if (segment !== undefined && retired === undefined) return undefined;
  if (retired?.tab === CONVERSATIONS_TAB) return { tabPath: CONVERSATIONS_PATH, search: view.search };

  const chips = readCommunicationChips(new URLSearchParams([...stored, ...chipEntries(retired?.chips)]));
  const kept = writeListChips(communicationChipGroups, chips, new URLSearchParams());
  for (const parameter of KEPT_PARAMETERS) {
    const value = stored.get(parameter);
    if (value !== null) kept.set(parameter, value);
  }
  return { tabPath: COMMUNICATIONS_PATH, search: kept.toString() };
}

function chipEntries(chips: ListChipSelection | undefined): [string, string][] {
  return Object.entries(chips ?? {}).flatMap(([parameter, values]) => values.map((value): [string, string] => [parameter, value]));
}

/**
 * The page's Default view, declared once: the inbox with no chip. Its bare path is the view, so a
 * plain visit opens the reader's starred or last view, else this one.
 */
export const COMMUNICATIONS_CURRENT_VIEW: CurrentTableViewOptions = {
  tableKey: "communications",
  basePath: COMMUNICATIONS_PATH,
  keepWhatStillExists: keepCommunicationsView,
};
